import {
  Checkbox,
  Icon,
  InlineStack,
  Stack,
  Text,
  TextArea,
  TextLabel,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { Tag } from '@customerio/hydra-ui-api';
import { GenerateDescriptionButton } from '../components/generate-description-button';
import { TagEditor } from '../components/tag-editor';
import { descriptionCounter, descriptionError } from '../utils/editing';
import { getLabelForItem } from '../utils/item';
import type { DataIndexItemKind } from '../utils/types';

/**
 * Editing what the workspace knows about an item, in place in the drawer.
 *
 * The three things a workspace owns about a row: what it is for, how it is
 * filed, and whether it should be redacted. Everything else on the drawer
 * comes from ingestion and is read-only wherever it appears.
 */

const DESCRIPTION_HELP = 'Descriptions help align team members on what an attribute is and how best to use it.';
const TAGS_HELP =
  'Tags help categorize and search for this attribute more easily. Tags can reflect usage, teams, systems, or anything that suits your unique needs.';
const SENSITIVE_HELP =
  'Check this box if the attribute contains personally identifiable information (PII) or sensitive data. This may restrict visibility and trigger enhanced protections. Users without the right permission will not be able to see this attribute.';

/** What is being edited, before any of it is saved. */
export type ItemDetailsDraft = {
  description: string;
  tagIds: number[];
  isSensitive: boolean;
};

type EditDetailsProps = {
  workspaceId: number;
  kind: DataIndexItemKind;
  name: string;
  /** Absent for a person attribute or an event, which belong to no object type. */
  objectTypeId?: number;
  /** Every tag the workspace has, so the picker can offer one the item does not carry. */
  tags: readonly Tag[];
  draft: ItemDetailsDraft;
  onChange: (next: ItemDetailsDraft | ((current: ItemDetailsDraft) => ItemDetailsDraft)) => void;
  /** Whether the plan and the attribute allow marking it sensitive at all. */
  canBeMarkedSensitive: boolean;
  /** False for a reserved name, or a viewer without the permission. */
  canEdit: boolean;
  isSaving: boolean;
  /** Reports that a tag is being created, which the footer's Save waits on. */
  onNaming: (isNaming: boolean) => void;
};

export function EditDetails({
  workspaceId,
  kind,
  name,
  objectTypeId,
  tags,
  draft,
  onChange,
  canBeMarkedSensitive,
  canEdit,
  isSaving,
  onNaming,
}: EditDetailsProps) {
  const error = descriptionError(draft.description);
  const isDisabled = isSaving || !canEdit;

  return (
    <Stack gap="200">
      <Stack gap="050">
        <LabelWithHelp label="Description" help={DESCRIPTION_HELP} />
        <TextArea
          ariaLabel="Description text area"
          value={draft.description}
          placeholder="Description…"
          description={error == null ? descriptionCounter(draft.description) : ''}
          error={error}
          isDisabled={isDisabled}
          rows={3}
          onInput={(event) => onChange({ ...draft, description: event.target.value })}
        />
        <div>
          <GenerateDescriptionButton
            workspaceId={workspaceId}
            type={getLabelForItem(kind)}
            name={name}
            objectTypeId={objectTypeId}
            isDisabled={isDisabled || error != null}
            onGenerated={(description) => onChange({ ...draft, description })}
          />
        </div>
      </Stack>

      <Stack gap="050">
        <LabelWithHelp label="Tags" help={TAGS_HELP} />
        <TagEditor
          workspaceId={workspaceId}
          tagIds={draft.tagIds}
          tags={tags.filter((tag) => draft.tagIds.includes(tag.id))}
          canEdit={canEdit}
          hasPermission={canEdit}
          onSave={() => Promise.resolve(true)}
          bulk={{
            tagIds: draft.tagIds,
            onChange: (next) => onChange((current) => ({ ...current, tagIds: next(current.tagIds) })),
            onNaming,
          }}
        />
      </Stack>

      {canBeMarkedSensitive ? (
        <InlineStack alignBlock="center" gap="050">
          <Checkbox
            ariaLabel="Make attribute sensitive"
            isChecked={draft.isSensitive}
            isDisabled={isDisabled}
            onCheckedChange={(isSensitive: boolean) => onChange({ ...draft, isSensitive })}
          />
          <Text>Make sensitive</Text>
          <Tooltip content={SENSITIVE_HELP}>
            <Icon name="help" size="sm" color="subtle" />
          </Tooltip>
        </InlineStack>
      ) : null}
    </Stack>
  );
}

type LabelWithHelpProps = {
  label: string;
  help: string;
};

function LabelWithHelp({ label, help }: LabelWithHelpProps) {
  return (
    <InlineStack alignBlock="center" gap="050">
      <TextLabel>{label}</TextLabel>
      <Tooltip content={help}>
        <Icon name="help" size="sm" color="subtle" />
      </Tooltip>
    </InlineStack>
  );
}
